<template>
  <TolModal
    :title="$t('projects.eventTypes.details.billing.add')"
    :submit-button-text="$t('projects.details.labels.save')"
    @close="clearFormAndClose"
    @submit="save"
  >
    <template v-slot:body>
      <form>
        <div class="form-group">
          <div class="billing-container">
            <div class="billing-dropdown-container">
              <label>
                {{ $t('projects.eventTypes.details.billing.activeLabourBillingType') }}
              </label>
              <DropdownSelect
                :menu-items="labourRatesTypes"
                :selected-item="billing.labour_rates.active_billing_type"
                class="bordered billing-dropdown"
                @select-item="billing.labour_rates.active_billing_type = $event"
              />
            </div>
            <div
              v-if="billing.labour_rates.active_billing_type === 'ACTUAL'"
              class="billing-values"
            >
              <div class="form-control billing-values-column">
                <label>
                  {{ $t('projects.eventTypes.details.billing.isPay3HoursMinimumEnabled') }}
                </label>
                <Checkbox
                  size="tiny"
                  :checked="billing.labour_rates.is_pay_3_hours_minimum_enabled"
                  @change="
                    billing.labour_rates.is_pay_3_hours_minimum_enabled = !billing.labour_rates
                      .is_pay_3_hours_minimum_enabled
                  "
                />
              </div>
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.billing.isBill3HoursMinimumEnabled') }}
                </label>
                <Checkbox
                  size="tiny"
                  :checked="billing.labour_rates.is_bill_3_hours_minimum_enabled"
                  @change="
                    billing.labour_rates.is_bill_3_hours_minimum_enabled = !billing.labour_rates
                      .is_bill_3_hours_minimum_enabled
                  "
                />
              </div>
            </div>
            <div
              v-if="billing.labour_rates.active_billing_type === 'FIXED_PER_TECH'"
              class="billing-values"
            >
              <div class="form-control billing-values-column">
                <label>
                  {{ $t('projects.eventTypes.details.billing.fixedPayPerTechnicianAmount') }}
                </label>
                <InputField
                  v-model="billing.labour_rates.fixed_pay_per_technician_amount"
                  input-type="number"
                  class="bordered"
                  :class="{ error: formErrors['fixed_pay_per_technician_amount'] }"
                  @input="billing.labour_rates.fixed_pay_per_technician_amount = $event"
                />
              </div>
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.billing.fixedBillPerTechnicianAmount') }}
                </label>
                <InputField
                  v-model="billing.labour_rates.fixed_bill_per_technician_amount"
                  input-type="number"
                  class="bordered"
                  :class="{ error: formErrors['fixed_labour_bill_per_technician_amount'] }"
                  @input="billing.labour_rates.fixed_bill_per_technician_amount = $event"
                />
              </div>
            </div>
            <div
              v-if="billing.labour_rates.active_billing_type === 'FIXED_PER_EVENT'"
              class="billing-values"
            >
              <div class="form-control billing-values-column">
                <label>
                  {{
                    $t('projects.eventTypes.details.billing.isFixedPayPerEvent3HoursMinimumEnabled')
                  }}
                </label>
                <Checkbox
                  size="tiny"
                  :checked="billing.labour_rates.is_fixed_pay_per_event_3_hours_minimum_enabled"
                  @change="
                    billing.labour_rates.is_fixed_pay_per_event_3_hours_minimum_enabled = !billing
                      .labour_rates.is_fixed_pay_per_event_3_hours_minimum_enabled
                  "
                />
              </div>
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.billing.fixedBillPerEventAmount') }}
                </label>
                <InputField
                  v-model="billing.labour_rates.fixed_bill_per_event_amount"
                  input-type="number"
                  class="bordered"
                  :class="{ error: formErrors['fixed_labour_bill_per_event_amount'] }"
                  @input="billing.labour_rates.fixed_bill_per_event_amount = $event"
                />
              </div>
            </div>
            <div
              v-if="billing.labour_rates.active_billing_type === 'FIXED_PER_ASSET'"
              class="billing-values"
            >
              <div class="form-control billing-values-column">
                <label>
                  {{
                    $t('projects.eventTypes.details.billing.isFixedPayPerAsset3HoursMinimumEnabled')
                  }}
                </label>
                <Checkbox
                  size="tiny"
                  :checked="billing.labour_rates.is_fixed_pay_per_asset_3_hours_minimum_enabled"
                  @change="
                    billing.labour_rates.is_fixed_pay_per_asset_3_hours_minimum_enabled = !billing
                      .labour_rates.is_fixed_pay_per_asset_3_hours_minimum_enabled
                  "
                />
              </div>
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.billing.fixedBillPerAssetAmount') }}
                </label>
                <InputField
                  v-model="billing.labour_rates.fixed_bill_per_asset_amount"
                  input-type="number"
                  class="bordered"
                  :class="{ error: formErrors['fixed_bill_per_asset_amount'] }"
                  @input="billing.labour_rates.fixed_bill_per_asset_amount = $event"
                />
              </div>
            </div>
          </div>
          <div v-if="billing.travel_rates" class="billing-container">
            <div class="billing-dropdown-container">
              <label>
                {{ $t('projects.eventTypes.details.billing.activeTravelBillingType') }}
              </label>
              <DropdownSelect
                :menu-items="travelRatesTypes"
                :selected-item="billing.travel_rates.active_billing_type"
                class="bordered billing-dropdown"
                @select-item="billing.travel_rates.active_billing_type = $event"
              />
            </div>
            <div
              v-if="billing.travel_rates.active_billing_type === 'FIXED_PER_TECH'"
              class="billing-values"
            >
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.billing.fixedBillPerTechnicianAmount') }}
                </label>
                <InputField
                  v-model="billing.travel_rates.fixed_bill_per_technician_amount"
                  input-type="number"
                  class="bordered"
                  :class="{ error: formErrors['fixed_travel_bill_per_technician_amount'] }"
                  @input="billing.travel_rates.fixed_bill_per_technician_amount = $event"
                />
              </div>
            </div>
            <div
              v-if="billing.travel_rates.active_billing_type === 'FIXED_PER_EVENT'"
              class="billing-values"
            >
              <div class="form-control">
                <label>
                  {{ $t('projects.eventTypes.details.billing.fixedBillPerEventAmount') }}
                </label>
                <InputField
                  v-model="billing.travel_rates.fixed_bill_per_event_amount"
                  input-type="number"
                  class="bordered"
                  :class="{ error: formErrors['fixed_travel_bill_per_event_amount'] }"
                  @input="billing.travel_rates.fixed_bill_per_event_amount = $event"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </template>
  </TolModal>
</template>

<script>
import Checkbox from '@/app/components/Checkbox';
import DropdownSelect from '@/app/components/DropdownSelect';
import InputField from '@/app/components/InputField';
import TolModal from '@/app/components/TolModal';

export default {
  name: 'AddBillingModal',
  components: {
    Checkbox,
    DropdownSelect,
    InputField,
    TolModal
  },
  data() {
    return {
      billing: {
        labour_rates: {
          active_billing_type: null,
          is_pay_3_hours_minimum_enabled: null,
          is_bill_3_hours_minimum_enabled: null,
          fixed_pay_per_technician_amount: null,
          fixed_bill_per_technician_amount: null,
          is_fixed_pay_per_event_3_hours_minimum_enabled: null,
          fixed_bill_per_event_amount: null,
          is_fixed_pay_per_asset_3_hours_minimum_enabled: null,
          fixed_bill_per_asset_amount: null
        },
        travel_rates: {
          active_billing_type: null,
          fixed_bill_per_technician_amount: null,
          fixed_bill_per_event_amount: null
        }
      },
      labourRatesTypes: ['ACTUAL', 'FIXED_PER_TECH', 'FIXED_PER_EVENT', 'FIXED_PER_ASSET'],
      travelRatesTypes: ['ACTUAL', 'FIXED_PER_TECH', 'FIXED_PER_EVENT']
    };
  },
  computed: {
    formErrors() {
      return this.$store.getters.ProjectEventTypeBillingValidationErrors;
    }
  },
  methods: {
    clearFormAndClose() {
      this.billing = {
        labour_rates: {
          active_billing_type: null,
          is_pay_3_hours_minimum_enabled: false,
          is_bill_3_hours_minimum_enabled: false,
          fixed_pay_per_technician_amount: null,
          fixed_bill_per_technician_amount: null,
          is_fixed_pay_per_event_3_hours_minimum_enabled: false,
          fixed_bill_per_event_amount: null,
          is_fixed_pay_per_asset_3_hours_minimum_enabled: false,
          fixed_bill_per_asset_amount: null
        },
        travel_rates: {
          active_billing_type: null,
          fixed_bill_per_technician_amount: null,
          fixed_bill_per_event_amount: null
        }
      };
      this.$emit('close');
    },
    async save() {
      const payload = {
        type: 'BILLING',
        value: this.billing
      };
      this.$emit('save', payload);
    }
  }
};
</script>

<style scoped lang="scss">
.form-group > div {
  margin-bottom: 1rem;
}

.billing-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  .billing-dropdown-container {
    flex: 1;

    @media only screen and (min-width: 600px) {
      margin-right: 7.5rem;
      max-width: 12rem;
    }
  }

  .billing-values {
    display: flex;
    align-items: center;
    flex: 2;

    .billing-values-column {
      margin-right: 5rem;
    }
  }
}
</style>
